import React, { FC, memo, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { PreviewPlayerContext } from 'contexts/PreviewPlayerContext'
import { NWShortCutContext } from 'contexts/NWShortCutContext'
import type Konva from 'konva'
import { useSelector } from 'react-redux'
import { selectIsResizingEditor } from 'selectors/useInterface'
import { createImage } from '../features/konva-shapes/createImage'
import { getReferenceVideoAsset } from '../selectors'
import { waitFrame } from '../Util'

export const PreviewPlayerContextProvider: FC = memo(({ children }) => {
  const [ canvasSource, setCanvasSource ] = useState<Konva.Stage | undefined>(undefined)
  const [ imageSource, setImageSource ] = useState<HTMLImageElement | undefined>(undefined)
  const [ videoSource, setVideoSource ] = useState<HTMLVideoElement | undefined>(undefined)
  const [ previewPlayer, setPreviewPlayer ] = useState<HTMLDivElement | null>(null)

  const isResizing = useSelector(selectIsResizingEditor)
  const referenceAsset = useSelector(getReferenceVideoAsset)

  const previewPlayerBox = useRef<HTMLDivElement | null>(null)

  const onSetPreviewPlayer: React.RefCallback<HTMLDivElement> = useCallback(ref => {
    setPreviewPlayer(ref)
  }, [ setPreviewPlayer ])

  const { registerNWGlobalHotKey, unRegisterNWGlobalHotKey } = useContext(NWShortCutContext)

  const handleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen()
      unRegisterNWGlobalHotKey()
    } else if (previewPlayer) {
      previewPlayer.requestFullscreen()
      registerNWGlobalHotKey()
    }
  }, [ registerNWGlobalHotKey, unRegisterNWGlobalHotKey, previewPlayer ])

  const saveFrame = (data: string, fileName = 'frame') => {
    const link = document.createElement('a')
    link.href = data
    link.download = fileName
    link.click()
    URL.revokeObjectURL(link.href)
    link.remove()
  }

  const captureFrame = useCallback((
    isMedia: boolean,
    onCaptured?: (dataUrl: string, fileName: string) => void,
    mimeType = 'image/jpeg'
  ) => {
    const handleFrame = (dataUrl: string, fileName: string) => {
      if (onCaptured) {
        onCaptured(dataUrl, fileName)
      } else {
        saveFrame(dataUrl, fileName)
      }
    }

    const ext = mimeType === 'image/png' ? 'png' : 'jpg'

    if (isMedia) {
      if (videoSource) {
        const canvas = document.createElement('canvas')
        canvas.width = videoSource.videoWidth
        canvas.height = videoSource.videoHeight
        const ctx = canvas.getContext('2d')

        if (ctx) {
          ctx.drawImage(videoSource, 0, 0, canvas.width, canvas.height)
          window.requestAnimationFrame(() => {
            handleFrame(canvas.toDataURL(mimeType, 0.9), `source-video-frame.${ext}`)
            canvas.remove()
          })
        }
      }

      // if (imageSource?.src) {
      //   fetch(imageSource.src)
      //     .then(response => response.blob())
      //     .then(blob => new Promise<string>((resolve, reject) => {
      //       const reader = new FileReader()
      //       reader.onloadend = () => resolve(reader.result as string)
      //       reader.onerror = reject
      //       reader.readAsDataURL(blob)
      //     }))
      //     .then(data => saveFrame(data, 'image-source.jpeg'))
      // }
      if (imageSource) {
        const size = {
          width: imageSource.naturalWidth,
          height: imageSource.naturalHeight,
        }
        createImage(imageSource.src, { size, opacity: 1, outlineWidth: undefined, outlineColor: undefined })
          .then((shape: Konva.Image) => {
            handleFrame(shape.toDataURL({ mimeType, quality: 0.9 }), `source-image.${ext}`)
          })
      }
    }

    if (!isMedia && canvasSource) {
      // eslint-disable-next-line no-underscore-dangle
      const isHidden = (layer: Konva.Layer) => layer.getCanvas()._canvas.style.visibility === 'hidden'
      const layers = (Object.values(canvasSource.getLayers()) as Konva.Layer[])
        .filter(layer => layer.name && layer.name() !== 'transition') as Konva.Layer[]

      const pixelRatio = referenceAsset?.width ? referenceAsset.width / canvasSource.width() : 1

      waitFrame(3)
        .then(() => layers.forEach(layer => isHidden(layer) && layer.hide()))
        .then(() => waitFrame())
        .then(() => handleFrame(canvasSource.toDataURL({
          mimeType,
          quality: 0.9,
          pixelRatio,
        }), `timeline-frame.${ext}`))
        .then(() => {
          layers.forEach(layer => isHidden(layer) && layer.show())
        })
    }
  }, [ videoSource, canvasSource, imageSource, referenceAsset ])

  const previewPlayerBounds = useMemo(() => (
    previewPlayer?.getBoundingClientRect()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [ previewPlayer, isResizing ])

  const values = useMemo(() => ({
    previewPlayer,
    previewPlayerBox,
    onSetPreviewPlayer,
    imageSource,
    canvasSource,
    videoSource,
    setImageSource,
    setCanvasSource,
    setVideoSource,
    handleFullscreen,
    captureFrame,
    previewPlayerBounds,
  }), [
    previewPlayer,
    previewPlayerBox,
    onSetPreviewPlayer,
    imageSource,
    canvasSource,
    videoSource,
    setImageSource,
    setCanvasSource,
    setVideoSource,
    handleFullscreen,
    captureFrame,
    previewPlayerBounds,
  ])

  return (
    <PreviewPlayerContext.Provider value={values}>
      {children}
    </PreviewPlayerContext.Provider>
  )
})
